// src/pages/logistica/ConversionCajas.jsx
import { useEffect, useState } from 'react';
import api, { kpiService } from '../../services/api';

export default function ConversionCajas() {
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardandoUpc, setGuardandoUpc] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  async function cargar() {
    try {
      setCargando(true);
      const res = await api.get('/api/logistica/conversion-cajas');
      setFilas(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => { cargar(); }, []);

  async function handleSincronizar() {
    try {
      setSincronizando(true);
      setMensaje(null);
      // Reutiliza el sync ya registrado en kpis.js para Caducidades — esta
      // pantalla lee de la misma tabla (kpi_caducidades_lotes), así que no
      // hace falta un endpoint de sync aparte.
      await kpiService.sync('caducidades-lotes');
      await cargar();
      setMensaje({ tipo: 'ok', texto: '✓ Sincronizado con NetSuite.' });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo sincronizar con NetSuite.' });
    } finally {
      setSincronizando(false);
    }
  }

  function handleCambiar(codigoUpc, valor) {
    setFilas((prev) => prev.map((f) => (f.codigo_upc === codigoUpc ? { ...f, unidades_por_caja: valor } : f)));
  }

  async function handleGuardar(codigoUpc) {
    const fila = filas.find((f) => f.codigo_upc === codigoUpc);
    try {
      setGuardandoUpc(codigoUpc);
      setMensaje(null);
      await api.put(`/api/logistica/conversion-cajas/${encodeURIComponent(codigoUpc)}`, {
        articulo: fila.articulo,
        unidades_por_caja: fila.unidades_por_caja,
      });
      setMensaje({ tipo: 'ok', texto: `✓ ${fila.articulo} actualizado.` });
      cargar(); // recarga para reordenar (los ya capturados bajan de la lista de pendientes)
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || `No se pudo guardar ${codigoUpc}.` });
    } finally {
      setGuardandoUpc(null);
    }
  }

  if (cargando) return <div style={styles.contenedor}>Cargando...</div>;

  const pendientes = filas.filter((f) => !f.unidades_por_caja).length;

  return (
    <div style={styles.contenedor}>
      <div style={styles.encabezado}>
        <div>
          <h1 style={styles.titulo}>Conversión Piezas → Cajas</h1>
          <p style={styles.subtitulo}>
            Usada para calcular las Cajas del KPI de Caducidades. Cada artículo puede traer una cantidad distinta de piezas por caja.
            {pendientes > 0 && (
              <span style={styles.pendientesAviso}> {pendientes} artículo{pendientes === 1 ? '' : 's'} sin capturar.</span>
            )}
          </p>
        </div>
        <button onClick={handleSincronizar} disabled={sincronizando} style={styles.botonSync}>
          {sincronizando ? 'Sincronizando...' : '↻ Sincronizar con NetSuite'}
        </button>
      </div>

      {mensaje && <div style={{ ...styles.mensaje, color: mensaje.tipo === 'ok' ? '#3E7C5A' : '#B23A48' }}>{mensaje.texto}</div>}

      <div style={styles.tarjeta}>
        {filas.map((f) => {
          const faltaCapturar = !f.unidades_por_caja;
          return (
            <div key={f.codigo_upc} style={{ ...styles.fila, ...(faltaCapturar ? styles.filaPendiente : {}) }}>
              <div>
                <div style={styles.articuloNombre}>{f.articulo}</div>
                <div style={styles.upc}>{f.codigo_upc}</div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="number"
                  step="1"
                  min="1"
                  placeholder="—"
                  value={f.unidades_por_caja ?? ''}
                  onChange={(e) => handleCambiar(f.codigo_upc, e.target.value)}
                  style={{ ...styles.input, ...(faltaCapturar ? styles.inputPendiente : {}) }}
                />
                <span style={styles.unidad}>pzas / caja</span>
                <button
                  onClick={() => handleGuardar(f.codigo_upc)}
                  disabled={guardandoUpc === f.codigo_upc || !f.unidades_por_caja}
                  style={styles.botonGuardar}
                >
                  {guardandoUpc === f.codigo_upc ? '...' : 'Guardar'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  contenedor: { fontFamily: 'Inter, sans-serif', padding: '24px', color: '#3A2E42', maxWidth: '640px' },
  encabezado: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', marginBottom: '4px' },
  botonSync: { flexShrink: 0, padding: '9px 14px', fontSize: '12px', fontWeight: 600, color: '#fff', background: '#6A2C75', border: 'none', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap' },
  titulo: { fontFamily: 'Playfair Display, serif', color: '#2A1830', marginBottom: '4px' },
  subtitulo: { fontSize: '12px', color: '#8A7690', marginBottom: '18px' },
  pendientesAviso: { color: '#B23A48', fontWeight: 600 },
  mensaje: { fontSize: '13px', marginBottom: '14px', fontWeight: 600 },
  tarjeta: { background: '#fff', border: '1px solid #E6DDD2', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 14px rgba(74,30,92,0.07)', display: 'flex', flexDirection: 'column', gap: '14px' },
  fila: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', paddingBottom: '14px', borderBottom: '1px solid #F0E9E0' },
  filaPendiente: { background: '#FDF3F1', margin: '0 -18px', padding: '10px 18px', borderRadius: '8px' },
  articuloNombre: { fontSize: '14px', fontWeight: 700, color: '#2A1830' },
  upc: { fontSize: '11px', color: '#8A7690', marginTop: '2px' },
  input: { width: '90px', padding: '7px 10px', fontSize: '13px', border: '1px solid #D6A644', borderRadius: '6px', fontFamily: 'Inter, sans-serif', background: '#FFFDF7' },
  inputPendiente: { borderColor: '#B23A48' },
  unidad: { fontSize: '11px', color: '#8A7690', whiteSpace: 'nowrap' },
  botonGuardar: { padding: '6px 14px', fontSize: '12px', fontWeight: 600, color: '#6E4F80', background: '#EFE9F3', border: '1px solid #A98BB8', borderRadius: '6px', cursor: 'pointer' },
};